import React, { createContext, useContext, useState, ReactNode } from "react"

export enum FlashMessageType {
  success = "success",
  error = "error",
  info = "info",
}

interface ShowFlashMessageParams {
  message: string
  type?: FlashMessageType
}

interface FlashMessageContextType {
  flashMessage: string | null
  flashType: FlashMessageType
  showFlashMessage: (params: ShowFlashMessageParams) => void
  dismissFlashMessage: () => void
}

const FlashMessageContext = createContext<FlashMessageContextType | undefined>(undefined)

export const useFlashMessage = (): FlashMessageContextType => {
  const context = useContext(FlashMessageContext)
  if (!context) {
    throw new Error("useFlashMessage must be used within a FlashMessageProvider")
  }
  return context
}

export const FlashMessageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [flashMessage, setFlashMessage] = useState<string | null>(null)
  const [flashType, setFlashType] = useState<FlashMessageType>(FlashMessageType.info)

  const showFlashMessage = ({ message, type = FlashMessageType.info }: ShowFlashMessageParams) => {
    setFlashMessage(message)
    setFlashType(type)
  }

  const dismissFlashMessage = () => {
    setFlashMessage(null)
  }

  return (
    <FlashMessageContext.Provider
      value={{
        flashMessage,
        flashType,
        showFlashMessage,
        dismissFlashMessage,
      }}
    >
      {children}
    </FlashMessageContext.Provider>
  )
}
